import React from 'react';
import type { CheckboxProps as ReactAriaCheckboxProps } from 'react-aria-components';
import { Checkbox as AriaCheckbox } from 'react-aria-components';

import { mergeClassNames } from '@/utils/mergeClassNames';
import styles from './Checkbox.module.scss';

export type CheckboxProps = ReactAriaCheckboxProps & {
  /** The value of the input element, used when submitting an HTML form. See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefvalue | MDN}. */
  value?: string;
  /** The name of the input element, used when submitting an HTML form. See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefname | MDN}. */
  name?: string;
};

/**
 * A checkbox allows a user to select multiple items from a list of individual items, or to mark one individual item as selected.
 * Built using the [React Aria's Checkbox component](https://react-spectrum.adobe.com/react-aria/Checkbox.html).
 *
 * Note: If there are a group of multiple checkboxes, use `CheckboxGroup`.
 *
 * ```ts
 * <Checkbox defaultSelected={true}>label</Checkbox>
 * ```
 */
export const Checkbox = ({
  children,
  className,
  isRequired,
  validationBehavior = 'native',
  ...props
}: CheckboxProps) => {
  return (
    <AriaCheckbox
      className={(renderProps) =>
        mergeClassNames([styles.root, className], renderProps)
      }
      data-zds
      validationBehavior={validationBehavior}
      data-has-no-visible-label={
        React.Children.count(children) === 0 ? true : undefined
      }
      {...props} // eslint-disable-line react/jsx-props-no-spreading
    >
      {(checkboxRenderProps) => (
        <>
          <div className={styles.checkbox} data-zds>
            <svg viewBox="0 0 18 18" aria-hidden="true" data-zds>
              {checkboxRenderProps.isIndeterminate ? (
                <rect x={0} y={7} width={18} height={4} />
              ) : (
                checkboxRenderProps.isSelected && (
                  <polyline points="2 9 8 14 16 4" />
                )
              )}
            </svg>
          </div>
          <div className={styles.label}>
            {typeof children === 'function'
              ? children(checkboxRenderProps)
              : children}
            {isRequired && (
              <span className={styles.requiredText}>required</span>
            )}
          </div>
        </>
      )}
    </AriaCheckbox>
  );
};
